import { forwardRef, useState } from 'react'

import classNames from 'classnames'

import { useNavigation } from '@web/hooks/useNavigation'
import { useAbTestNClick } from '@web/libs/contexts/AbTestEventContextProvider'
import { useContent } from '@web/libs/contexts/ContentContext'
import { useClickCodeDebugMode } from '@web/libs/dev/clickCodeDebugMode.state'
import { useOverlayText } from '@web/libs/dev/useOverlayText'
import sendClickLog from '@web/libs/nlog/click/sendClickLog'
import styles from '@web/ui/components/Clickable/Clickable.module.scss'
import { ClickableProps } from '@web/ui/components/Clickable/context'

const MyClickable = forwardRef<HTMLButtonElement, ClickableProps>(function MyClickable(
  {
    children,
    role,
    className,
    disabled = false,
    href = '',
    replace = false,
    newWindow = false,
    onClick,
    nClickCode,
    nClickCid,
    nClickRank,
    nClickBusinessId,
    nClickPlaceId,
    nClickReviewId,
    nClickVisitId,
    stopPropagation = false,
    ...props
  },
  ref,
) {
  /**
   * 비즈니스 로직 추가
   */

  const { push } = useNavigation()

  const content = useContent()
  const { trackNClickEvent } = useAbTestNClick()

  const [isHovered, setIsHovered] = useState(false)

  const [clickCodeDebugMode] = useClickCodeDebugMode()

  const { OverlayText } = useOverlayText({ showable: isHovered && !!clickCodeDebugMode, text: nClickCode })

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = e => {
    e.preventDefault()

    if (stopPropagation) e.stopPropagation()

    if (disabled) return

    const isClickEvent = e?.type === 'click'
    if (nClickCode && isClickEvent) {
      sendClickLog({
        event: e,
        area: nClickCode,
        rank: nClickRank,
        contentId: nClickCid ?? content.id,
        contentType: content.type,
        placeId: nClickPlaceId ?? content.placeId,
        visitId: nClickVisitId ?? content.visitId,
        reviewId: nClickReviewId ?? content.reviewId,
        businessId: nClickBusinessId ?? content.businessId,
      })
      trackNClickEvent(nClickCode)
    }

    if (onClick) {
      onClick(e)
      return
    }

    if (href) {
      if (newWindow) {
        push({ url: href, openWindow: true })
        return
      }

      if (replace) {
        push({ url: href, replace: true })
        return
      }

      push({ url: href })
    }
  }

  return (
    <button
      ref={ref}
      type="button"
      role={role}
      className={classNames(styles.clickable, className)}
      disabled={disabled}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...props}
    >
      {children}
      {OverlayText}
    </button>
  )
})

export default MyClickable
